import { useEffect, useState } from "react";
import { useI18n } from "../i18n";
import { loadCustomFields, loadCustomValues, saveCustomValues, type CustomFieldDef } from "../data";

interface Props {
  companyId: string;
  personId: string;
  employmentId: string;
  canEdit: boolean;
}

type Values = Record<string, unknown>;

/** The company's own fields on a profile, readable by anyone who can see the profile. */
export function CustomDetails({ companyId, personId, employmentId, canEdit }: Props) {
  const { t, pick } = useI18n();
  const [defs, setDefs] = useState<CustomFieldDef[]>([]);
  const [values, setValues] = useState<{ person: Values; employment: Values }>({ person: {}, employment: {} });
  const [draft, setDraft] = useState<{ person: Values; employment: Values } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const [d, v] = await Promise.all([loadCustomFields(companyId), loadCustomValues(personId, employmentId)]);
    setDefs(d.filter((x) => x.active));
    setValues(v);
  }
  useEffect(() => { void refresh().catch((e) => setError(e.message)); }, [personId, employmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (defs.length === 0) return null;

  const show = (d: CustomFieldDef, v: unknown) =>
    v === undefined || v === null || v === "" ? "—" : d.field_type === "boolean" ? (v ? t("yes") : t("no")) : String(v);

  function setValue(d: CustomFieldDef, raw: string | boolean) {
    if (!draft) return;
    const v = typeof raw === "boolean" ? raw : raw === "" ? null : d.field_type === "number" ? Number(raw) : raw;
    setDraft({ ...draft, [d.entity]: { ...draft[d.entity], [d.key]: v } });
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const strip = (o: Values) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== null && v !== ""));
      await saveCustomValues("persons", personId, strip(draft.person));
      await saveCustomValues("employments", employmentId, strip(draft.employment));
      setDraft(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2>{t("customDetails")}</h2>
        {canEdit && !draft && <button type="button" className="btn secondary small" onClick={() => setDraft(values)}>{t("editDetails")}</button>}
      </div>
      {error && <p className="alert danger" role="alert">{error}</p>}
      {!draft ? (
        <dl className="kv-list">
          {defs.map((d) => (
            <div className="kv" key={d.id}>
              <dt>{pick(d.label_en, d.label_ar)}</dt>
              <dd>{show(d, values[d.entity][d.key])}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <div className="stack">
          <div className="field-row">
            {defs.map((d) => {
              const v = draft[d.entity][d.key];
              const id = `cv-${d.key}`;
              const label = `${pick(d.label_en, d.label_ar)}${d.required ? " *" : ""}`;
              if (d.field_type === "boolean")
                return <label className="check" key={d.id}><input id={id} type="checkbox" checked={v === true} onChange={(e) => setValue(d, e.target.checked)} /> {label}</label>;
              if (d.field_type === "select")
                return (
                  <label className="field" key={d.id}><span>{label}</span>
                    <select id={id} value={(v as string) ?? ""} onChange={(e) => setValue(d, e.target.value)}>
                      <option value="">—</option>
                      {(d.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select></label>
                );
              return (
                <label className="field" key={d.id}><span>{label}</span>
                  <input id={id} type={d.field_type === "number" ? "number" : d.field_type === "date" ? "date" : "text"}
                    value={v === null || v === undefined ? "" : String(v)} onChange={(e) => setValue(d, e.target.value)} /></label>
              );
            })}
          </div>
          <div className="actions">
            <button type="button" className="btn secondary" onClick={() => setDraft(null)} disabled={busy}>{t("cancel")}</button>
            <button type="button" className="btn primary" onClick={save} disabled={busy}>{t("save")}</button>
          </div>
        </div>
      )}
    </section>
  );
}
